import { EventEmitter, inject, Injectable, Input, signal, WritableSignal } from '@angular/core';
import { FeatureCollection } from 'geojson';
import maplibregl from 'maplibre-gl';
import Spiderfy from '@nazka/map-gl-js-spiderfy'

import { MOD_VISUALIZATION_BASEMAPS } from '../../domain/constants/basemaps.constant';
import { MapService } from './map.service';
import { StorageRepositoryService } from './storage.repository.service';
import { Basemap, Overlay } from '../../domain/types';
import { MOD_VISUALIZATION_OVERLAYS } from '../../domain/constants/overlays.constant';
import { GEOSERVER_URL, URL_GEO_API } from 'src/environments/environment.prod';
import { GEOSERVER_WORKSPACE } from '../../domain/constants/geoserver.constant';
import { AlertsVisualizationMvtBuilder } from '../../utils/mvt-request-builder';
import { FilterSignalService } from '../signals/filter-signals.service';
import { Filter } from '../../domain/interfaces/Filter.interface';
import { FilterService } from './filter.service';
import { getFilterPayload } from '../../utils/filter/filter-format';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class MapLayersService {

  private storageService: StorageRepositoryService = inject(StorageRepositoryService);
  private mapService: MapService = inject(MapService);
  private readonly filterSignalService: FilterSignalService = inject(FilterSignalService);
  private readonly filterService = inject(FilterService)
  private readonly router = inject(Router)

  map: maplibregl.Map;

  isAlertsLayerVisible: WritableSignal<boolean> = signal(true);
  selectedAlerts: WritableSignal<{
    featureCollection?: FeatureCollection
    fitOn?: boolean
  }> = signal(null);
  alertsLayerUpdated = new EventEmitter<void>();
  legendsColor: WritableSignal<any> = signal<any>('color')
  spiderfy: any

  geojson = {
    'type': 'FeatureCollection',
    'features': []
  }

  linestring = {
    'type': 'Feature',
    'geometry': {
      'type': 'LineString',
      'coordinates': []
    }
  }

  clearLayer(layerName) {
    if (this.map && this.map.getLayer(layerName)) {
      this.map.removeLayer(layerName);
      this.map.removeSource(layerName);
    }

    let layerOutline = layerName.endsWith('Outline')
      ? layerName.slice(0, -8)
      : `${layerName} Outline`

    if (this.map && this.map.getLayer(layerOutline)) {
      this.map.removeLayer(layerOutline);
      this.map.removeSource(layerOutline);
    }
  }

  initializeLayers(map: maplibregl.Map) {
    this.map = map;
    const defaultBasemaps = MOD_VISUALIZATION_BASEMAPS

    // loading last used basemap
    const storedBasemaps = this.storageService.load('basemaps', 'local', []);
    const initialBasemapIndex = storedBasemaps.findIndex(basemap => basemap.isVisible);

    if (initialBasemapIndex === -1) {
      const defaultBasemapIndex = MOD_VISUALIZATION_BASEMAPS.findIndex(basemap => basemap.name === 'Satellite');
      this.updateBasemapLayer(defaultBasemaps[defaultBasemapIndex], defaultBasemapIndex);
    } else {
      this.updateBasemapLayer(storedBasemaps[initialBasemapIndex], initialBasemapIndex);
    }

    // loading last used overlays
    const storedOverlays = this.storageService.load('overlays', 'local', []);
    const arraySize = storedOverlays.length;
    const invertedOverlays = storedOverlays.slice().reverse();

    storedOverlays.forEach((item, index) => {
      const mapIndex = arraySize - index;
      //this.updateOverlayItem(item, mapIndex);
      this.updateOverlayItem(invertedOverlays[index], mapIndex);
    });

  }

  loadSelectedAlerts(params: {
    featureCollection?: FeatureCollection;
    fitOn?: boolean;
  }) {
    if (this.mapService.map.getSource('selectedAlert-overlay-source')) {
      this.mapService.map.removeLayer('selectedAlert-overlay-layer');
      this.mapService.map.removeSource('selectedAlert-overlay-source');
    }

    this.selectedAlerts.set(params);

    if (params.featureCollection) {
      this.mapService.map.addSource('selectedAlert-overlay-source', {
        type: 'geojson',
        data: params.featureCollection.features[0]
      });

      this.mapService.map.addLayer({
        'id': 'selectedAlert-overlay-layer',
        'type': 'fill',
        'source': 'selectedAlert-overlay-source',
        'paint': {
          'fill-color': '#FFFFFF',
          'fill-opacity': 0.6,
          'fill-outline-color': '#FFFFFF',
        }
      });

      if (this.isAlertsLayerVisible()) {
        const bounds = this.computeBounds(params.featureCollection.features);
        this.mapService.map.fitBounds(bounds, { maxZoom: 15, padding: { bottom: 100, right: 200 } });

        if (params.fitOn) {
          this.mapService.map.fitBounds(bounds, {
            maxZoom: 17, padding: {
              bottom: 100,
              right: 400,
              left: 100,
              top: 100
            }, duration: 1000, offset: [0, -20]
          });
        }
      }
    }
  }

  private computeBounds(features: GeoJSON.Feature[]): maplibregl.LngLatBounds | null {
    if (!features.length) return null;

    const bounds = new maplibregl.LngLatBounds();

    features.forEach(feature => {
      const geometry = feature;
      this.expandBoundsForGeometry(geometry, bounds);
    });

    return bounds.isEmpty() ? null : bounds;
  }

  private expandBoundsForGeometry(geometry: any, bounds: maplibregl.LngLatBounds) {
    switch (geometry.type) {
      case 'Polygon':
        geometry.coordinates.flat(2).forEach(coord => bounds.extend(coord as unknown as [number, number]));
        break;
      case 'MultiPolygon':
        geometry.coordinates.flat(2).forEach(coord => bounds.extend(coord as unknown as [number, number]));
        break;
    }
  }

  updateBasemapLayer(updatedBasemaps: Basemap, basemapIndex: number) {
    let basemap

    // removing current basemap
    this.map.getStyle().layers.forEach(layer => {
      if (layer.id.endsWith('-basemap-layer')) {
        this.map.removeLayer(layer.id);
      }
    });
    this.map.getStyle().sources && Object.keys(this.map.getStyle().sources).forEach(source => {
      if (source.endsWith('-basemap-source')) {
        this.map.removeSource(source);
      }
    });

    if (!updatedBasemaps.getLayer) {
      basemap = MOD_VISUALIZATION_BASEMAPS[basemapIndex];
    } else {
      basemap = updatedBasemaps;
    }
    const { source, layer } = basemap.getLayer();

    // this is to add the basemap layer to the bottom of the layers list
    const allLayers = this.map.getStyle().layers;
    const firstLayer = allLayers.length > 0 ? allLayers[0].id : null

    // adding new basemap
    this.map.addSource(`${updatedBasemaps.name.toLowerCase()}-basemap-source`, source);
    this.map.addLayer(layer, firstLayer);
  }

  updateOverlayItem(item: Overlay, mapIndex: number) {
    if (!item.layer) {
      item.layer = MOD_VISUALIZATION_OVERLAYS[mapIndex];
    } else {
      // Remove the layer if it exists
      if (this.mapService.map.getLayer(item.id + '-overlay-layer')) {
        this.clearAlertsLayer();
      }

      // Update the layer visibility
      this.updateLayer(item, item.isVisible);
    }
  }

  private updateLayer(overlay: Overlay, isVisible: boolean) {
    const sourceId = `${overlay.id}-overlay-source`;
    const layerId = `${overlay.id}-overlay-layer`;

    // Special handling for alerts layer
    if (layerId === 'alerts-overlay-layer') {
      // Update the alert visibility in the filter signal service
      this.filterSignalService.alertVisibility.set(isVisible);

      if (isVisible) {
        this.createAlertsLayer(this.filterService.filter());
      } else {
        this.clearAlertsLayer();
      }
      return;
    }

    // Handle other overlay types
    if (isVisible) {
      // Check if the source already exists
      if (!this.mapService.map.getSource(sourceId)) {
        this.mapService.map.addSource(sourceId, {
          type: 'raster',
          tiles: [
            `${GEOSERVER_URL}/wms?service=WMS&version=1.3.0&request=GetMap&layers=${overlay.layers}&styles=&bbox={bbox-epsg-3857}&width=750&height=768&srs=EPSG:3857&format=image/png&transparent=true`
          ]
        });
      }

      // Check if the layer already exists
      if (!this.mapService.map.getLayer(layerId)) {
        this.mapService.map.addLayer({
          id: layerId,
          type: 'raster',
          source: sourceId
        });
      }
    } else {
      // Remove the layer if it is not visible
      if (this.mapService.map.getLayer(layerId)) {
        this.mapService.map.removeLayer(layerId);
      }

      // Remove the source if it is not used by any other layers
      if (this.mapService.map.getSource(sourceId)) {
        const isSourceUsed = this.mapService.map.getStyle().layers.some(l => (l as any).source === sourceId);
        if (!isSourceUsed) {
          this.mapService.map.removeSource(sourceId);
        }
      }
    }
  }

  // Add a new method to cleanly remove the alerts layer
  clearAlertsLayer() {
    if (this.mapService.map && this.mapService.map.getLayer('alerts-overlay-layer')) {
      this.mapService.map.removeLayer('alerts-overlay-layer');
      this.mapService.map.removeLayer('count');
      this.map.removeImage('cluster');
      this.spiderfy.unspiderfyAll();
      this.mapService.map.removeSource('alerts-overlay-source');
    }
  }

  async createAlertsLayer(filter: Filter) {
    // First clear any existing alert layers to prevent duplicates
    this.clearAlertsLayer();

    // Only proceed if alerts should be visible
    if (!this.filterSignalService.alertVisibility()) {
      return;
    }

    const baseUrl = `${URL_GEO_API}/mvt/geoserver_communities/{z}/{x}/{y}`;
    const params = new URLSearchParams({
      geo_column: 'geom',
      columns: 'id,color,connection_color,contract_color,energy_kit_color,association_color,router_color',
      filter: AlertsVisualizationMvtBuilder(getFilterPayload(filter)),
    });

    const url = `${baseUrl}?${params.toString()}`;

    const baseUrl2 = `${URL_GEO_API}/geojson/geoserver_communities`;
    const params2 = new URLSearchParams({
      geom_column: 'geom',
      columns: 'id,color,connection_color,contract_color,energy_kit_color,association_color,router_color',
      filter: AlertsVisualizationMvtBuilder(getFilterPayload(filter)),
    })

    const url2 = `${baseUrl2}?${params2.toString()}`

    this.mapService.map.addSource('alerts-overlay-source', {
      type: 'geojson',
      data: url2,
      cluster: true,
      clusterRadius: 0.1
    })

    const { data: image } = await this.map.loadImage(
      '../../../../assets/img/test/test_icon2.png',
    );
    this.map.addImage('cluster', image, { sdf: true });

    this.map.addLayer({
      'id': 'alerts-overlay-layer',
      'type': 'symbol',
      'source': 'alerts-overlay-source',
      'layout': {
        'icon-image': 'cluster',
        'icon-size': 0.8,
        'icon-allow-overlap': true
      },
      'paint': {
        'icon-color': ['case', ['has', 'point_count'], '#000', ['get', this.legendsColor()]]
      }
    })

    this.map.addLayer({
      'id': 'count',
      'type': 'symbol',
      'source': 'alerts-overlay-source',
      'layout': {
        'text-field': ['get', 'point_count'],
        'text-size': 10,
        'text-allow-overlap': true,
      },
      'paint': {
        'text-color': '#fff'
      }
    })

    this.spiderfy = new Spiderfy(this.map, {
      onLeafClick: f => this.leafClick(f.properties.id),
      onLeafHover: f => this.leafHover(f),
      minZoomLevel: 12,
      zoomIncrement: 2,
      closeOnLeafClick: false,
      circleSpiralSwitchover: 2,
      circleOptions: {
        leavesSeparation: 100,
        leavesOffset: [0, 0],
      },
      spiralOptions: {
        legLengthStart: 25,
        legLengthFactor: 3,
        leavesSeparation: 30,
        leavesOffset: [0, 0],
      },
      spiderLegsAreHidden: true,
      spiderLegsWidth: 2,
      spiderLegsColor: '#000000',
      maxLeaves: 500,
      spiderLeavesPaint: {
        'icon-color': ['get', this.legendsColor()]
      }
    });

    this.spiderfy.applyTo('alerts-overlay-layer');

    // this.mapService.map.addSource('alerts-overlay-source', {
    //   type: 'vector',
    //   tiles: [url]
    // });
    // this.map.addLayer({
    //   'id': 'alerts-overlay-layer',
    //   'type': 'circle',
    //   'source': 'alerts-overlay-source',
    //   'source-layer': 'geoserver_communities',
    //   'paint': {
    //     'circle-color': {
    //       property: this.legendsColor(),
    //       type: 'identity'
    //     },
    //     'circle-opacity': 0.5,
    //     'circle-stroke-color': {
    //       property: this.legendsColor(),
    //       type: 'identity'
    //     },
    //     'circle-stroke-width': 2
    //   }
    // })
  }

  leafClick(communityId: any) {
    this.router.navigate(['/consulta', communityId]);
    // console.log(featureId.geometry.coordinates);
    // const style = this.map.getStyle();
    // const layers = style.layers;
    // for (const layer of layers) {
    //   console.log(layer)
    // }
  }

  leafHover(f: any) {
    if (f) {
      this.mapService.map.getCanvas().style.cursor = 'pointer';
    } else {
      this.mapService.map.getCanvas().style.cursor = '';
    }
  }

  addMeasurerLayer() {
    this.mapService.map.addSource('geojson', {
      'type': 'geojson',
      'data': this.geojson as GeoJSON.FeatureCollection
    })

    this.mapService.map.addLayer({
      'id': 'measure-lines',
      'type': 'line',
      'source': 'geojson',
      'layout': {
        'line-cap': 'round',
        'line-join': 'round',
      },
      'paint': {
        'line-color': '#B58A35',
        'line-width': 2.5
      },
      filter: ['in', '$type', 'LineString']
    })

    this.mapService.map.addLayer({
      'id': 'measure-points',
      'type': 'circle',
      'source': 'geojson',
      'paint': {
        'circle-radius': 5,
        'circle-color': '#855B22',
      },
      filter: ['in', '$type', 'Point']
    })


    if (!this.mapService.map.getSource('distance-labels')) {
      this.mapService.map.addSource('distance-labels', {
        'type': 'geojson',
        'data': this.geojson as GeoJSON.FeatureCollection
      });

      this.mapService.map.addLayer({
        id: 'distance-labels',
        type: 'symbol',
        source: 'distance-labels',
        layout: {
          'text-field': [
            'format',
            ['get', 'segment'],
            '\n',
            {},
            ['get', 'total']
          ],
          'text-size': 12,
          'text-offset': [3, 1.5],
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': '#fff',
          'text-halo-color': '#000',
          'text-halo-width': 0.3,
          'text-halo-blur': 2
        }
      });
    }
  }
}
